import React from "react"
import { useDispatch } from "react-redux"
import { ref, onValue } from "firebase/database"

import { ApolloError } from "@apollo/client"
import { GraphQLFormattedError } from "graphql"
import { sdk9db } from "../../../firebase"
import { logError } from "../../../utils/errors"
import { setSnackbar } from "../../../actions/globalUiActions"
import Loading from "../../template-parts/Loading"
import { InstructorLesson } from "../../../types"
import type { LessonCardState } from "./LessonCard"
import LessonForm from "./LessonForm"

function getUserFriendlyErrorMessage(
  e: ApolloError | GraphQLFormattedError,
): string {
  if (e === undefined) {
    return ""
  }

  const errors = "graphQLErrors" in e ? e.graphQLErrors : []
  if (errors.length > 0) {
    return errors.map(getUserFriendlyErrorMessage).join("\n")
  }

  return e.message ?? ""
}

interface LessonEditWrapperProps {
  instructorId: string
  lessonId: string
  onCloseCallback: () => void
  onStatusChanged?: (status: LessonCardState["formStatus"]) => void
  onSavedCallback?: (lesson: InstructorLesson) => void
}

const LessonEditWrapper = ({
  instructorId,
  lessonId,
  onCloseCallback,
  onStatusChanged,
  onSavedCallback,
}: LessonEditWrapperProps) => {
  const dispatch = useDispatch()
  const [lesson, setLesson] = React.useState(null)

  React.useEffect(() => {
    const lessonSlotsUnsub = onValue(
      ref(sdk9db, `lessonSlotsV2/${instructorId}/${lessonId}`),
      (lessonSnap) => {
        const lessonObj = lessonSnap.val()
        if (lessonObj) {
          lessonObj.id = lessonSnap.key
          setLesson(lessonObj)
        }
      },
    )

    return () => {
      if (lessonSlotsUnsub) lessonSlotsUnsub()
    }
  }, [instructorId, lessonId])

  const lessonSavedCallback = (updatedLesson: InstructorLesson) => {
    dispatch(setSnackbar("Lesson changes saved.", "success"))
    onCloseCallback()
    onSavedCallback?.(updatedLesson)
  }

  const lessonNotSavedCallback = (error: Error) => {
    logError(error)
    const message = `Lesson not saved: ${getUserFriendlyErrorMessage(error)}`
    dispatch(setSnackbar(message, "error"))
  }

  if (!lesson) {
    return <Loading />
  }

  return (
    <div>
      <h4 style={{ marginTop: 0 }}>Edit Lesson Slot</h4>
      <LessonForm
        lessonSlot={lesson}
        lessonSlotSavedCallback={lessonSavedCallback}
        saveErrorCallback={lessonNotSavedCallback}
        onStatusChanged={onStatusChanged}
      />
    </div>
  )
}

export default LessonEditWrapper
